import { useState, useCallback } from 'react';
import { AutoComplete } from 'antd';
import { loadItemsSafe } from './proformaApi';
import { useTypeaheadSearch } from './useTypeaheadSearch';
import { parseItemDesc, composeItemDesc, parseThicknessMm, resolveProcessRateForThickness } from './proformaHelpers';

// ════════════════════════════════════════════════════════════════════
// SmartItemDescCell — the ONE proforma item-description field.
//
// A single AutoComplete that does BOTH: Item-Master glass search (type text →
// pick a glass, which fills the line) AND the "#process@rate/uom" picker (type
// `#` → thickness-aware process options inserted as `#Name@rate/UOM`). Typing is
// parsed by `parseItemDesc` into base + tags → `itemDesc` (composed display) +
// `processPricing`. This is the exact field used by the proforma grid's ITEM DESC
// column AND (via the same component) by the LAMI / DGU builder windows, so the
// two behave identically — never two separate fields.
//
// Extracted from ProformaLineGrid so the grid and the builder modals share ONE
// implementation (no duplication, no circular import).
// ════════════════════════════════════════════════════════════════════

// Build the "#process" picker options. The rate is resolved by the line's glass
// thickness (parsed from the item description): thickness-specific rate if the
// Process Master defines one for that thickness, else the default rate.
export function processTagOptions(processes, thicknessMm) {
  return (Array.isArray(processes) ? processes : [])
    .map((p) => ({
      name: p.processName || p.name || p.abbreviation || p.label || '',
      rate: resolveProcessRateForThickness(p, thicknessMm),
      uom: (p.uomCode || p.uom || 'SQF').toUpperCase()
    }))
    .filter((p) => p.name);
}

// UOM colour family for the #-picker badge: running-feet (RFT/RMT) vs area
// (SQF/SQM…) vs per-piece (PCS/SET…). Display only.
export function uomFamily(uom) {
  const u = String(uom || '').toUpperCase();
  if (u === 'RFT' || u === 'RMT' || u === 'MTR') return 'rft';
  if (u === 'PCS' || u === 'SET' || u === 'QTY' || u === 'NOS') return 'pcs';
  return 'sqf'; // SQF / SQFT / SQM / SQMT / blank → area
}

// One row in the "#" process picker: name · rate · colour-coded UOM badge.
export function ProcessOptionLabel({ name, rate, uom }) {
  return (
    <div className="pfg-proc-opt">
      <span className="pfg-proc-name">{name}</span>
      {rate > 0 ? <span className="pfg-proc-rate">₹{rate}</span> : <span className="pfg-proc-rate pfg-proc-norate">—</span>}
      <span className={`pfg-proc-uom pfg-uom-${uomFamily(uom)}`}>{uom}</span>
    </div>
  );
}

// A line has an RFT/RMT (running-feet) process → the edge picker applies.
export function hasRftProcess(pricing) {
  return (Array.isArray(pricing) ? pricing : [])
    .some((p) => ['RFT', 'RMT'].includes(String((p && p.uom) || '').toUpperCase()) && Number(p && p.rate) > 0);
}

export function SmartItemDescCell({ cellId, row, dropdowns, onCommit, onPickItem, onPickCombo, onRftAdded, readOnly, indent, size, placeholder }) {
  const [editing, setEditing] = useState(false);
  // While focused, a LOCAL buffer is the single source of truth for what's shown
  // (mirrors DimCell). The grid renders rows from useDeferredValue(lines), so the
  // `row` prop LAGS the DOM during fast typing; feeding that lagging value back
  // into this controlled input made React reset the box mid-keystroke and DROP
  // characters — spaces most visibly (two words fused). Driving the input from
  // immediate local state while editing decouples typing from the deferred render.
  const [localValue, setLocalValue] = useState('');
  const raw = (row.rawItemDesc != null && row.rawItemDesc !== '') ? row.rawItemDesc : (row.itemDesc || '');
  const display = row.itemDesc || raw;
  const shown = editing ? localValue : display;
  const procList = (dropdowns && dropdowns.processes) || [];

  const commitRaw = useCallback((v) => {
    const p = parseItemDesc(v);
    const patch = { rawItemDesc: v, itemDesc: composeItemDesc(p.base, p.tags), processPricing: p.pricing, itemId: null };
    // Track the glass thickness from the description (e.g. "12MM …" → 12) so the
    // THICKNESS column + weight stay in sync. Only update on a valid token, so a
    // partial keystroke or a manual override is never clobbered to 0.
    const thk = parseThicknessMm(p.base);
    if (thk != null && thk > 0) patch.thicknessMm = thk;
    onCommit(patch);
    // Auto-open the edge picker when this commit ADDS an RFT/RMT process that
    // wasn't there before (a running-feet price is meaningless without a run).
    if (onRftAdded && hasRftProcess(p.pricing) && !hasRftProcess(row.processPricing)) {
      onRftAdded(row.refUuid);
    }
  }, [onCommit, onRftAdded, row.processPricing, row.refUuid]);

  // Stage 6PL-1c — the picker asks about the SAME customer the dropdowns were
  // fetched for (dropdowns.priceList), so the rate shown on pick is the rate
  // the server will agree with on save. No extra prop: taking the context off
  // the payload the rates came with makes the two impossible to desynchronise.
  const priceCtx = (dropdowns && dropdowns.priceList) || null;
  const ctxCustomerId = priceCtx && priceCtx.customerId ? priceCtx.customerId : null;
  const ctxAsOfDate = priceCtx && priceCtx.asOfDate ? priceCtx.asOfDate : null;

  const fetchItems = useCallback(async (q) => {
    const { rows } = await loadItemsSafe(q, { customerId: ctxCustomerId, asOfDate: ctxAsOfDate });
    return rows.slice(0, 15).map((it) => ({
      value: it.itemDesc || it.itemName || it.itemCode || '',
      // A combo / set item is flagged so picking it expands all its components.
      label: `${it.isSetItem ? '◧ ' : ''}${it.itemName || it.itemDesc}${it.isSetItem ? ` · combo (${it.componentCount || 0})` : (it.hsncode ? ' · HSN ' + it.hsncode : '')}`,
      item: it
    }));
  }, [ctxCustomerId, ctxAsOfDate]);
  const { options: opts, setOptions: setOpts, run, cancel } = useTypeaheadSearch(fetchItems, { delay: 250, minChars: 1 });

  const search = useCallback((text) => {
    const t = text == null ? '' : String(text);
    const hash = t.lastIndexOf('#');
    if (hash >= 0) {
      cancel();
      const prefix = t.slice(0, hash);
      const q = t.slice(hash + 1).trim().toLowerCase();
      // Glass thickness from the base description (text before the first #) →
      // resolves each process rate by thickness (falls back to default rate).
      const thicknessMm = parseThicknessMm(prefix);
      const procs = processTagOptions(procList, thicknessMm).filter((p) => !q || p.name.toLowerCase().includes(q));
      setOpts(procs.slice(0, 12).map((p) => ({
        // value drives filtering + what gets inserted — UNCHANGED.
        value: `${prefix}#${p.name}${p.rate > 0 ? '@' + p.rate + '/' + p.uom : ''} `,
        // label is display-only: name · rate · colour-coded UOM badge.
        label: <ProcessOptionLabel name={p.name} rate={p.rate} uom={p.uom} />
      })));
    } else { run(t); }
  }, [procList, run, cancel, setOpts]);

  return (
    <AutoComplete id={cellId} size={size} className="w-full pfg-ac" popupClassName="pfg-ac-pop pfg-ac-pop-wide" value={shown} disabled={readOnly}
      // Let the suggestion popup size to its content (a comfortable min/max width in
      // CSS) instead of matching the narrow ITEM DESC cell, so long descriptions
      // are fully readable when selecting.
      popupMatchSelectWidth={false}
      style={indent ? { paddingInlineStart: indent } : undefined}
      options={opts} filterOption={false} onSearch={search}
      onFocus={() => { setEditing(true); setLocalValue(raw); setOpts([]); }}
      onBlur={() => { setEditing(false); cancel(); setOpts([]); }}
      onSelect={(_v, opt) => {
        if (!opt || !opt.item) return;
        // A combo expands into its component lines; a normal item fills one line.
        if (opt.item.isSetItem && onPickCombo) onPickCombo(opt.item);
        else if (onPickItem) onPickItem(opt.item);
      }}
      // The local buffer keeps the box in sync instantly (decoupled from the deferred
      // grid re-price); the commit runs SYNCHRONOUSLY so the priced preview never lags
      // behind the live rows (a deferred commit blanked charge/area cells mid-entry).
      onChange={(v) => { setLocalValue(v); commitRaw(v); }}
      placeholder={placeholder || 'Item Master…  #process@rate/uom'} />
  );
}

export default SmartItemDescCell;
